import { parseEnvironmentIdFromUrl } from "@/utils/connect-query/environment-id";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { create, toBinary } from "@bufbuild/protobuf";
import type {
  DescMessage,
  DescMethodStreaming,
  DescMethodUnary,
  MessageInitShape,
  MessageShape,
} from "@bufbuild/protobuf";
import { ConnectError } from "@connectrpc/connect";
import type { Code, Transport } from "@connectrpc/connect";

const DASHBOARD_FIXTURE_CAPTURE_VERSION = 1;
const DASHBOARD_FIXTURE_CAPTURE_UPDATED_EVENT = "chalk-dashboard-fixture-capture-updated";
const DEFAULT_MAX_CAPTURED_MESSAGE_BYTES = 10 * 1024 * 1024;

const SENSITIVE_METHOD_NAME_PATTERNS = [
  /secretvalue/i,
  /upsertsecret/i,
  /createservicetoken/i,
  /deleteservicetoken/i,
  /updateservicetoken/i,
  /verificationtoken/i,
  /^login/i,
  /saml/i,
  /oauth/i,
  // Integration RPCs (Insert/Update/Test/Get/ListIntegrationsAndSecrets, GetIntegrationValue)
  // carry plaintext datasource credentials in IntegrationConfigValue.literal.
  /integration/i,
] as const;

declare global {
  interface Window {
    __CHALK_DASHBOARD_FIXTURE_CAPTURE__?: DashboardFixtureCaptureState;
  }
}

export interface SerializedDashboardFixtureMessage {
  base64: string;
  byteLength: number;
  hash: string;
}

export interface DashboardFixtureCaptureRecord {
  id: string;
  baseUrl: string;
  durationMs: number;
  environmentId: string | null;
  error?: DashboardFixtureError;
  methodKind: "unary" | "server_streaming" | "client_streaming" | "bidi_streaming";
  methodName: string;
  projectId: string | null;
  request?: SerializedDashboardFixtureMessage;
  requestMessages?: SerializedDashboardFixtureMessage[];
  response?: SerializedDashboardFixtureMessage;
  responseMessages?: SerializedDashboardFixtureMessage[];
  route: string;
  serviceName: string;
  startedAt: string;
  status: "success" | "error";
}

export interface DashboardFixtureCaptureSkippedRecord {
  baseUrl: string;
  environmentId: string | null;
  methodKind: string;
  methodName: string;
  projectId: string | null;
  reason: string;
  route: string;
  serviceName: string;
  skippedAt: string;
}

export interface DashboardFixtureError {
  code?: Code;
  message: string;
  name: string;
}

interface DashboardFixtureCaptureState {
  pendingCount: number;
  records: DashboardFixtureCaptureRecord[];
  sequence: number;
  skipped: DashboardFixtureCaptureSkippedRecord[];
  startedAt: string;
}

export interface DashboardFixtureCaptureSummary {
  enabled: boolean;
  recordCount: number;
  skippedCount: number;
  startedAt?: string;
}

export interface DashboardFixtureCaptureBundle {
  downloadedAt: string;
  records: DashboardFixtureCaptureRecord[];
  skipped: DashboardFixtureCaptureSkippedRecord[];
  source: {
    href?: string;
    userAgent?: string;
  };
  startedAt: string;
  summary: {
    recordCount: number;
    skippedCount: number;
  };
  version: typeof DASHBOARD_FIXTURE_CAPTURE_VERSION;
}

export function isDashboardFixtureCaptureEnabled(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.localStorage.getItem(LocalStorageKey.DASHBOARD_FIXTURE_CAPTURE_ENABLED) === "true";
}

export function startDashboardFixtureCapture({ reset = true }: { reset?: boolean } = {}): void {
  if (typeof window === "undefined") {
    return;
  }
  if (reset) {
    clearDashboardFixtureCapture();
  }
  window.localStorage.setItem(LocalStorageKey.DASHBOARD_FIXTURE_CAPTURE_ENABLED, "true");
  getDashboardFixtureCaptureState();
  notifyDashboardFixtureCaptureUpdated();
}

export function stopDashboardFixtureCapture(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(LocalStorageKey.DASHBOARD_FIXTURE_CAPTURE_ENABLED);
  notifyDashboardFixtureCaptureUpdated();
}

export function clearDashboardFixtureCapture(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.__CHALK_DASHBOARD_FIXTURE_CAPTURE__ = undefined;
  window.sessionStorage.removeItem(LocalStorageKey.DASHBOARD_FIXTURE_CAPTURE_CRAWL_STATE);
  window.sessionStorage.removeItem(LocalStorageKey.DASHBOARD_FIXTURE_CAPTURE_STATE);
  notifyDashboardFixtureCaptureUpdated();
}

export function getDashboardFixtureCaptureSummary(): DashboardFixtureCaptureSummary {
  if (typeof window === "undefined") {
    return { enabled: false, recordCount: 0, skippedCount: 0 };
  }
  const state =
    window.__CHALK_DASHBOARD_FIXTURE_CAPTURE__ ?? readPersistedDashboardFixtureCaptureState();
  if (state != null) {
    window.__CHALK_DASHBOARD_FIXTURE_CAPTURE__ = state;
  }
  return {
    enabled: isDashboardFixtureCaptureEnabled(),
    recordCount: state?.records.length ?? 0,
    skippedCount: state?.skipped.length ?? 0,
    startedAt: state?.startedAt,
  };
}

export function makeDashboardFixtureCaptureBundle(): DashboardFixtureCaptureBundle {
  const state = getDashboardFixtureCaptureState();
  return {
    version: DASHBOARD_FIXTURE_CAPTURE_VERSION,
    startedAt: state.startedAt,
    downloadedAt: new Date().toISOString(),
    source: {
      href: typeof window !== "undefined" ? window.location.href : undefined,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    },
    summary: {
      recordCount: state.records.length,
      skippedCount: state.skipped.length,
    },
    records: state.records,
    skipped: state.skipped,
  };
}

export function getDashboardFixtureCapturePendingCount(): number {
  if (typeof window === "undefined") {
    return 0;
  }

  const state =
    window.__CHALK_DASHBOARD_FIXTURE_CAPTURE__ ?? readPersistedDashboardFixtureCaptureState();
  if (state != null) {
    window.__CHALK_DASHBOARD_FIXTURE_CAPTURE__ = state;
  }
  return state?.pendingCount ?? 0;
}

export function getDashboardFixtureCaptureFilename(): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `chalk-dashboard-fixture-${timestamp}.json`;
}

export function addDashboardFixtureCaptureEventListener(listener: () => void): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }
  window.addEventListener(DASHBOARD_FIXTURE_CAPTURE_UPDATED_EVENT, listener);
  return () => window.removeEventListener(DASHBOARD_FIXTURE_CAPTURE_UPDATED_EVENT, listener);
}

export function wrapTransportWithDashboardFixtureCapture(
  transport: Transport,
  { baseUrl }: { baseUrl: string },
): Transport {
  return {
    unary: async (method, signal, timeoutMs, header, input, contextValues) => {
      if (!isDashboardFixtureCaptureEnabled()) {
        return await transport.unary(method, signal, timeoutMs, header, input, contextValues);
      }

      const finishCaptureRequest = beginDashboardFixtureCaptureRequest();
      const startedAtMs = Date.now();
      const snapshot = makeCaptureSnapshot(baseUrl, method);
      try {
        const shouldSkip = getMethodSkipReason(method);
        if (shouldSkip != null) {
          recordSkippedCapture(snapshot, shouldSkip);
          return await transport.unary(method, signal, timeoutMs, header, input, contextValues);
        }

        let request: SerializedDashboardFixtureMessage | undefined;
        try {
          request = serializeMessage(method.input, input);
        } catch (error) {
          recordSkippedCapture(snapshot, `request serialization failed: ${errorToMessage(error)}`);
          return await transport.unary(method, signal, timeoutMs, header, input, contextValues);
        }

        if (request.byteLength > DEFAULT_MAX_CAPTURED_MESSAGE_BYTES) {
          recordSkippedCapture(
            snapshot,
            `request exceeded ${DEFAULT_MAX_CAPTURED_MESSAGE_BYTES} bytes`,
          );
          return await transport.unary(method, signal, timeoutMs, header, input, contextValues);
        }

        try {
          const response = await transport.unary(
            method,
            signal,
            timeoutMs,
            header,
            input,
            contextValues,
          );
          let serializedResponse: SerializedDashboardFixtureMessage;
          try {
            serializedResponse = serializeMessage(method.output, response.message);
          } catch (error) {
            recordSkippedCapture(
              snapshot,
              `response serialization failed: ${errorToMessage(error)}`,
            );
            return response;
          }

          if (serializedResponse.byteLength > DEFAULT_MAX_CAPTURED_MESSAGE_BYTES) {
            recordSkippedCapture(
              snapshot,
              `response exceeded ${DEFAULT_MAX_CAPTURED_MESSAGE_BYTES} bytes`,
            );
            return response;
          }

          recordCapture({
            ...snapshot,
            durationMs: Date.now() - startedAtMs,
            methodKind: method.methodKind,
            request,
            response: serializedResponse,
            status: "success",
          });
          return response;
        } catch (error) {
          recordCapture({
            ...snapshot,
            durationMs: Date.now() - startedAtMs,
            error: serializeError(error),
            methodKind: method.methodKind,
            request,
            status: "error",
          });
          throw error;
        }
      } finally {
        finishCaptureRequest();
      }
    },

    stream: async (method, signal, timeoutMs, header, input, contextValues) => {
      if (!isDashboardFixtureCaptureEnabled()) {
        return await transport.stream(method, signal, timeoutMs, header, input, contextValues);
      }

      const finishCaptureRequest = beginDashboardFixtureCaptureRequest();
      const startedAtMs = Date.now();
      const snapshot = makeCaptureSnapshot(baseUrl, method);
      const shouldSkip = getMethodSkipReason(method);
      if (shouldSkip != null) {
        try {
          recordSkippedCapture(snapshot, shouldSkip);
          return await transport.stream(method, signal, timeoutMs, header, input, contextValues);
        } finally {
          finishCaptureRequest();
        }
      }

      const requestMessages: SerializedDashboardFixtureMessage[] = [];
      const responseMessages: SerializedDashboardFixtureMessage[] = [];

      const capturedInput = captureStreamMessages(method.input, input, requestMessages, snapshot);

      try {
        const response = await transport.stream(
          method,
          signal,
          timeoutMs,
          header,
          capturedInput,
          contextValues,
        );

        return {
          ...response,
          message: captureResponseStreamMessages({
            baseSnapshot: snapshot,
            finishCaptureRequest,
            inputMessages: requestMessages,
            method,
            output: response.message,
            outputMessages: responseMessages,
            startedAtMs,
          }),
        };
      } catch (error) {
        recordCapture({
          ...snapshot,
          durationMs: Date.now() - startedAtMs,
          error: serializeError(error),
          methodKind: method.methodKind,
          requestMessages,
          status: "error",
        });
        finishCaptureRequest();
        throw error;
      }
    },
  };
}

interface CaptureSnapshot {
  baseUrl: string;
  environmentId: string | null;
  methodKind: DashboardFixtureCaptureRecord["methodKind"];
  methodName: string;
  projectId: string | null;
  route: string;
  serviceName: string;
  startedAt: string;
}

function getDashboardFixtureCaptureState(): DashboardFixtureCaptureState {
  if (typeof window === "undefined") {
    return {
      pendingCount: 0,
      records: [],
      sequence: 0,
      skipped: [],
      startedAt: new Date().toISOString(),
    };
  }

  if (window.__CHALK_DASHBOARD_FIXTURE_CAPTURE__ != null) {
    return window.__CHALK_DASHBOARD_FIXTURE_CAPTURE__;
  }

  const persistedState = readPersistedDashboardFixtureCaptureState();
  if (persistedState != null) {
    window.__CHALK_DASHBOARD_FIXTURE_CAPTURE__ = persistedState;
    return persistedState;
  }

  window.__CHALK_DASHBOARD_FIXTURE_CAPTURE__ = {
    pendingCount: 0,
    records: [],
    sequence: 0,
    skipped: [],
    startedAt: new Date().toISOString(),
  };
  persistDashboardFixtureCaptureState(window.__CHALK_DASHBOARD_FIXTURE_CAPTURE__);
  return window.__CHALK_DASHBOARD_FIXTURE_CAPTURE__;
}

function makeCaptureSnapshot(
  baseUrl: string,
  method: DescMethodUnary | DescMethodStreaming,
): CaptureSnapshot {
  const href = typeof window !== "undefined" ? window.location.href : "";
  return {
    baseUrl,
    environmentId: href ? parseEnvironmentIdFromUrl(href) : null,
    methodKind: method.methodKind,
    methodName: method.name,
    projectId: href.match(/\/projects\/([^/?#]+)/)?.[1] ?? null,
    route:
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : "",
    serviceName: method.parent.typeName,
    startedAt: new Date().toISOString(),
  };
}

function recordCapture(record: Omit<DashboardFixtureCaptureRecord, "id">): void {
  const state = getDashboardFixtureCaptureState();
  state.sequence += 1;
  state.records.push({
    id: `${state.sequence}-${record.serviceName}/${record.methodName}`,
    ...record,
  });
  persistDashboardFixtureCaptureState(state);
  notifyDashboardFixtureCaptureUpdated();
}

function recordSkippedCapture(snapshot: CaptureSnapshot, reason: string): void {
  const state = getDashboardFixtureCaptureState();
  state.skipped.push({
    ...snapshot,
    reason,
    skippedAt: new Date().toISOString(),
  });
  persistDashboardFixtureCaptureState(state);
  notifyDashboardFixtureCaptureUpdated();
}

function readPersistedDashboardFixtureCaptureState(): DashboardFixtureCaptureState | null {
  try {
    const raw = window.sessionStorage.getItem(LocalStorageKey.DASHBOARD_FIXTURE_CAPTURE_STATE);
    if (raw == null) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<DashboardFixtureCaptureState>;
    if (!Array.isArray(parsed.records) || !Array.isArray(parsed.skipped)) {
      return null;
    }

    return {
      pendingCount: 0,
      records: parsed.records as DashboardFixtureCaptureRecord[],
      sequence:
        typeof parsed.sequence === "number" ? parsed.sequence : Math.max(parsed.records.length, 0),
      skipped: parsed.skipped as DashboardFixtureCaptureSkippedRecord[],
      startedAt: typeof parsed.startedAt === "string" ? parsed.startedAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

function beginDashboardFixtureCaptureRequest(): () => void {
  const state = getDashboardFixtureCaptureState();
  state.pendingCount += 1;
  persistDashboardFixtureCaptureState(state);
  notifyDashboardFixtureCaptureUpdated();

  let finished = false;
  return () => {
    if (finished) {
      return;
    }
    finished = true;

    const nextState = getDashboardFixtureCaptureState();
    nextState.pendingCount = Math.max(0, nextState.pendingCount - 1);
    persistDashboardFixtureCaptureState(nextState);
    notifyDashboardFixtureCaptureUpdated();
  };
}

function persistDashboardFixtureCaptureState(state: DashboardFixtureCaptureState): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      LocalStorageKey.DASHBOARD_FIXTURE_CAPTURE_STATE,
      JSON.stringify(state),
    );
  } catch (error) {
    console.warn("[Dashboard fixture capture] failed to persist capture state", error);
  }
}

function notifyDashboardFixtureCaptureUpdated(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new Event(DASHBOARD_FIXTURE_CAPTURE_UPDATED_EVENT));
}

// Shared with the network log so every capture surface skips the same methods.
export function isSensitiveCaptureMethodName(methodName: string): boolean {
  return SENSITIVE_METHOD_NAME_PATTERNS.some((pattern) => pattern.test(methodName));
}

function getMethodSkipReason(method: DescMethodUnary | DescMethodStreaming): string | null {
  if (isSensitiveCaptureMethodName(method.name)) {
    return "method name matched sensitive capture skip list";
  }
  return null;
}

function serializeMessage(
  schema: DescMessage,
  message: MessageInitShape<DescMessage> | MessageShape<DescMessage>,
): SerializedDashboardFixtureMessage {
  const bytes = toBinary(schema, create(schema, message as MessageInitShape<DescMessage>));
  return {
    base64: bytesToBase64(bytes),
    byteLength: bytes.byteLength,
    hash: hashBytes(bytes),
  };
}

async function* captureStreamMessages<I extends DescMessage>(
  schema: I,
  input: AsyncIterable<MessageInitShape<I>>,
  output: SerializedDashboardFixtureMessage[],
  snapshot: CaptureSnapshot,
): AsyncIterable<MessageInitShape<I>> {
  for await (const message of input) {
    try {
      const serialized = serializeMessage(schema, message);
      if (serialized.byteLength <= DEFAULT_MAX_CAPTURED_MESSAGE_BYTES) {
        output.push(serialized);
      } else {
        recordSkippedCapture(
          snapshot,
          `stream request message exceeded ${DEFAULT_MAX_CAPTURED_MESSAGE_BYTES} bytes`,
        );
      }
    } catch (error) {
      recordSkippedCapture(
        snapshot,
        `stream request serialization failed: ${errorToMessage(error)}`,
      );
    }
    yield message;
  }
}

async function* captureResponseStreamMessages<I extends DescMessage, O extends DescMessage>({
  baseSnapshot,
  finishCaptureRequest,
  inputMessages,
  method,
  output,
  outputMessages,
  startedAtMs,
}: {
  baseSnapshot: CaptureSnapshot;
  finishCaptureRequest: () => void;
  inputMessages: SerializedDashboardFixtureMessage[];
  method: DescMethodStreaming<I, O>;
  output: AsyncIterable<MessageShape<O>>;
  outputMessages: SerializedDashboardFixtureMessage[];
  startedAtMs: number;
}): AsyncIterable<MessageShape<O>> {
  try {
    for await (const message of output) {
      try {
        const serialized = serializeMessage(method.output, message);
        if (serialized.byteLength <= DEFAULT_MAX_CAPTURED_MESSAGE_BYTES) {
          outputMessages.push(serialized);
        } else {
          recordSkippedCapture(
            baseSnapshot,
            `stream response message exceeded ${DEFAULT_MAX_CAPTURED_MESSAGE_BYTES} bytes`,
          );
        }
      } catch (error) {
        recordSkippedCapture(
          baseSnapshot,
          `stream response serialization failed: ${errorToMessage(error)}`,
        );
      }
      yield message;
    }

    recordCapture({
      ...baseSnapshot,
      durationMs: Date.now() - startedAtMs,
      methodKind: method.methodKind,
      requestMessages: inputMessages,
      responseMessages: outputMessages,
      status: "success",
    });
  } catch (error) {
    recordCapture({
      ...baseSnapshot,
      durationMs: Date.now() - startedAtMs,
      error: serializeError(error),
      methodKind: method.methodKind,
      requestMessages: inputMessages,
      responseMessages: outputMessages,
      status: "error",
    });
    throw error;
  } finally {
    finishCaptureRequest();
  }
}

function serializeError(error: unknown): DashboardFixtureError {
  if (error instanceof ConnectError) {
    return {
      code: error.code,
      message: error.rawMessage,
      name: "ConnectError",
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
    };
  }

  return {
    message: String(error),
    name: "Error",
  };
}

function errorToMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function bytesToBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(bytes).toString("base64");
  }

  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function hashBytes(bytes: Uint8Array): string {
  let hash = 0x811c9dc5;
  for (const byte of bytes) {
    hash ^= byte;
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
